import {
    ChangeEvent, useMemo, useState, useEffect,
} from 'react';
import {
    NSDropdown, NSInput, NSModal, NSRadioOption,
} from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import './CreateProjectModal.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket } from '@fortawesome/pro-regular-svg-icons';
import { useCreateSubprojectFromProForma } from 'queries/ProForma/useCreateSubprojectFromProForma';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import useAuthContext from 'contexts/AuthContext';
import { useLoader } from 'contexts/LoaderContext';
import useToast from 'hooks/useToast';
import { useGetDevelopmentProjects } from 'hooks/useGetDevelopmentProjects';
import RenderIf from 'components/RenderIf/RenderIf';
import { URLS } from 'services/urls';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';

const SELECTED_OPTION = {
    NEW: 'new',
    EXISTING: 'existing',
};

export interface ICreateProjectModalProps {
    isOpen: boolean;
    toggle: () => void;
}

const CreateProjectModal = ({ toggle, isOpen }: ICreateProjectModalProps) => {
    const { proForma, updateProFormaLocal } = useProFormaSocketContext();
    const { selectedOrganizationId } = useAuthContext();
    const { showLoader, hideLoader } = useLoader();
    const { showSuccess, showError } = useToast();

    const [selectedOption, setSelectedOption] = useState<typeof SELECTED_OPTION.NEW | typeof SELECTED_OPTION.EXISTING>(SELECTED_OPTION.NEW);
    const [newSiteName, setNewSiteName] = useState('');
    const [selectedSite, setSelectedSite] = useState<IOptionAnyValue | undefined>();
    const [projectName, setProjectName] = useState<string>('');

    const { mutate: createSubproject, isPending: isCreatingSubproject } = useCreateSubprojectFromProForma();

    const {
        data: existingDevelopmentProjects,
        isLoading: isLoadingDevelopmentProjects,
        refetch: refetchDevelopmentProjects,
    } = useGetDevelopmentProjects(selectedOrganizationId!, {
        enabled: Boolean(selectedOrganizationId),
    });

    useEffect(() => {
        if (isOpen && selectedOrganizationId) {
            refetchDevelopmentProjects();
        }
    }, [isOpen, selectedOrganizationId, refetchDevelopmentProjects]);

    const devSiteOptions = existingDevelopmentProjects && existingDevelopmentProjects.length ? existingDevelopmentProjects.map(site => ({
        label: site.projectName,
        value: site.correlationId,
    })) : [];

    const isCreateDisabled = useMemo(() => {
        if (!projectName?.trim()) return true;

        if (existingDevelopmentProjects?.length && selectedOption === SELECTED_OPTION.EXISTING) {
            return !selectedSite;
        }

        return !newSiteName.trim();
    }, [newSiteName, selectedSite, projectName, existingDevelopmentProjects, selectedOption]);

    const handleCreateProject = () => {
        if (!selectedOrganizationId || !proForma?.id || !proForma.dealId) return;

        showLoader({ message: 'Creating your project: ', highlightedText: projectName.trim() });

        createSubproject({
            organizationId: selectedOrganizationId,
            proFormaId: proForma.id,
            dealId: proForma.dealId,
            data: {
                saleDate: proForma.saleDate,
                projectName: projectName.trim(),
                developmentSiteName: selectedOption === SELECTED_OPTION.NEW ? newSiteName.trim() : undefined,
                existingDevelopmentSiteUuid: selectedOption === SELECTED_OPTION.EXISTING ? selectedSite?.value : undefined,
            },
        }, {
            onSuccess: data => {
                setTimeout(() => {
                    hideLoader();
                    toggle();
                    updateProFormaLocal({
                        proFormaStatusId: PRO_FORMA_STATUS.PROJECT_CREATED,
                        subprojectId: data?.correlationId ?? proForma?.subprojectId ?? null,
                    });
                    window.open(`${URLS.PROJECTS_APP.PROJECTS}/project_overview/${data?.id}`, '_blank');
                    showSuccess('Project created successfully');
                }, 2000);
            },
            onError: error => {
                hideLoader();
                showError('Failed to create project. Please try again.');
                console.error('Error creating project:', error);
            },
        });
    };

    useEffect(() => {
        if (proForma?.name && !projectName) {
            setProjectName(proForma.name);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proForma]);

    return (
        <NSModal
            toggle={toggle}
            isOpen={isOpen}
            modalHeaderText="Create project from deal"
            modalHeaderClassName="bg-primary text-white"
            modalFooterButtonText="Create project"
            modalFooterButtonFunction={handleCreateProject}
            modalFooterIsButtonFunctionDisabled={isCreateDisabled}
            modalFooterButtonIsLoading={isCreatingSubproject}
        >
            <div className="text-center mb-2">
                <div
                    className="d-inline-flex align-items-center justify-content-center bg-primary rounded-circle"
                    style={{ width: '37px', height: '37px' }}
                >
                    <FontAwesomeIcon icon={faRocket} className="text-white" size="lg" />
                </div>
                <div className="text-white font-18">
                    You're one step away from launching a new project!
                </div>
                <div className="font-12">
                    Confirm the development site and name your project.
                </div>
            </div>

            <div>
                <RenderIf isTrue={existingDevelopmentProjects && existingDevelopmentProjects.length > 0} isLoading={isLoadingDevelopmentProjects}>
                    <>
                        <div
                            className={`CreateProjectModal mb-2 cursor--pointer ${selectedOption === 'new' ? 'CreateProjectModal--selected' : ''}`}
                            onClick={() => setSelectedOption('new')}
                            role="presentation"
                        >
                            <div className="d-flex align-items-start p-2">
                                <div className="mr-2">
                                    <NSRadioOption
                                        id="new-site"
                                        name="site-selection"
                                        label=""
                                        checked={selectedOption === 'new'}
                                        onChange={() => setSelectedOption('new')}
                                    />
                                </div>
                                <div className="flex-grow-1">
                                    <div className="text-white font-weight-bold">New development site</div>
                                    {selectedOption === 'new' && (
                                        <div className="mt-2">
                                            <NSInput
                                                type="text"
                                                placeholder="Enter development site name"
                                                value={newSiteName}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => setNewSiteName(e.target.value)}
                                                name="newSiteName"
                                                required
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div
                            className={`CreateProjectModal mb-2 cursor--pointer ${selectedOption === 'existing' ? 'CreateProjectModal--selected' : ''}`}
                            onClick={() => setSelectedOption('existing')}
                            role="presentation"
                        >
                            <div className="d-flex align-items-start p-2">
                                <div className="mr-2">
                                    <NSRadioOption
                                        id="existing-site"
                                        name="site-selection"
                                        label=""
                                        checked={selectedOption === 'existing'}
                                        onChange={() => setSelectedOption('existing')}
                                    />
                                </div>
                                <div className="flex-grow-1">
                                    <div className="text-white font-weight-bold">Existing development site</div>
                                    {selectedOption === 'existing' && (
                                        <div className="mt-2">
                                            <NSDropdown
                                                options={devSiteOptions}
                                                selectedOption={selectedSite}
                                                handleSelectOption={option => setSelectedSite(option)}
                                                placeholder={isLoadingDevelopmentProjects
                                                    ? 'Loading development sites...' : 'Select existing development site'}
                                                isFullWidth
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                </RenderIf>

                <RenderIf isTrue={existingDevelopmentProjects && existingDevelopmentProjects.length === 0} isLoading={isLoadingDevelopmentProjects}>
                    <div className="mb-2">
                        <NSInput
                            label="Development site name"
                            type="text"
                            placeholder="Enter development site name"
                            value={newSiteName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setNewSiteName(e.target.value)}
                            name="newSiteName"
                            required
                        />
                        <div className="font-12">Once created, this development site can contain any future projects you create.</div>
                    </div>
                </RenderIf>
                <div>
                    <NSInput
                        label="Project name"
                        type="text"
                        placeholder="Enter project name"
                        value={projectName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
                        name="projectName"
                        required
                    />
                </div>
            </div>
        </NSModal>
    );
};

export default CreateProjectModal;
